/**
 * Growth · Loyalty Engine (Wave E).
 *
 * Full tier engine per contract: Discovery / Silver / Gold / Platinum / Atelier / Founders / VIP.
 * Every member is drillable — Member Card, Benefits, Progress, Upgrade rules,
 * Current Spend, Remaining Spend, Wallet-ready card. Reuses existing primitives.
 */
import { useMemo, useState } from "react";
import { Award } from "lucide-react";
import { Card, SectionHeader, TextInput, Button } from "../../components/lib";
import { Drawer, Field, Segmented, ListRow, TabBar } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { LOYALTY_TIERS, LOYALTY_MEMBERS, LoyaltyMember, LoyaltyTier } from "../../mocks/growthData";
import { CUSTOMERS_360 } from "../../mocks/customers360Data";

/** Contract-mandated tier ladder. Falls back to fixture data if fewer are seeded. */
const CANONICAL_TIERS: { key: string; name: string; minLtvEGP: number; benefits: string; color: string }[] = [
  { key: "discovery",  name: "Discovery",  minLtvEGP:      0, benefits: "Welcome mini + newsletter",           color: "var(--ph-ink-muted)" },
  { key: "silver",     name: "Silver",     minLtvEGP:  25000, benefits: "5% credit · early notice",             color: "#B0B0B0" },
  { key: "gold",       name: "Gold",       minLtvEGP:  75000, benefits: "10% credit · private launches",        color: "#D4AF37" },
  { key: "platinum",   name: "Platinum",   minLtvEGP: 150000, benefits: "15% credit · dedicated concierge",     color: "#8FA0AB" },
  { key: "atelier",    name: "Atelier",    minLtvEGP: 300000, benefits: "Custom perfumer session · lab visit",  color: "var(--ph-brand-primary)" },
  { key: "founders",   name: "Founders",   minLtvEGP: 600000, benefits: "Founders room · exclusive editions",   color: "var(--ph-brand-accent)" },
  { key: "vip",        name: "VIP",        minLtvEGP: 1000000,benefits: "White-glove · full annual concierge",  color: "var(--ph-danger)" },
];

function tierByLtv(ltvEGP: number) {
  return [...CANONICAL_TIERS].reverse().find((t) => ltvEGP >= t.minLtvEGP) || CANONICAL_TIERS[0];
}

function nextTier(cur: typeof CANONICAL_TIERS[number]) {
  const idx = CANONICAL_TIERS.findIndex((t) => t.key === cur.key);
  return CANONICAL_TIERS[idx + 1] ?? null;
}

type LoyTab = "card" | "benefits" | "progress" | "upgrade" | "spend" | "wallet" | "history" | "audit";

export default function Loyalty() {
  const [selected, setSelected] = useState<LoyaltyMember | null>(null);
  const [tab, setTab] = useState<string>("all");
  const [q, setQ] = useState("");
  const [wsTab, setWsTab] = useState<LoyTab>("card");

  // Merge canonical ladder with seeded tier stats (member counts) so nothing is lost.
  const tiers = useMemo(() => {
    return CANONICAL_TIERS.map((c) => {
      const seeded = (LOYALTY_TIERS as LoyaltyTier[]).find((t) => t.key === c.key);
      const memberCount = LOYALTY_MEMBERS.filter((m) => m.tier === c.key || m.tier === c.name).length;
      return { ...c, members: seeded?.members ?? memberCount };
    });
  }, []);

  const filtered = useMemo(() => LOYALTY_MEMBERS.filter((m) =>
    (tab === "all" || m.tier.toLowerCase() === tab) &&
    (!q || m.customer.toLowerCase().includes(q.toLowerCase())),
  ), [tab, q]);

  const drill = useMemo(() => {
    if (!selected) return null;
    const c = tierByLtv(selected.ltvEGP);
    const nxt = nextTier(c);
    const remainingEGP = nxt ? Math.max(0, nxt.minLtvEGP - selected.ltvEGP) : 0;
    const progressPct = nxt ? Math.min(100, ((selected.ltvEGP - c.minLtvEGP) / Math.max(1, nxt.minLtvEGP - c.minLtvEGP)) * 100) : 100;
    const customer360 = CUSTOMERS_360.find((cx) => cx.name === selected.customer);
    return { cur: c, next: nxt, remainingEGP, progressPct, customer360 };
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="gr-loyalty">
      {/* ── Tier ladder ─────────────────────────────────────────── */}
      <Card testId="gr-l-summary">
        <SectionHeader eyebrow="Loyalty Engine · Tier ladder" title={`${tiers.length} tiers · ${LOYALTY_MEMBERS.length} members`} action={<Award size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {tiers.map((t) => (
            <button key={t.key} data-testid={`gr-tier-${t.key}`} onClick={() => setTab(t.key)}
              className="text-left p-3"
              style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", border: `1px solid ${t.color}22` }}
            >
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: t.color }}>{t.name}</div>
              <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{t.members}</div>
              <div className="text-[10px] mt-1" style={{ color: "var(--ph-ink-muted)" }}>≥ EGP {(t.minLtvEGP / 1000).toFixed(0)}K</div>
              <div className="text-[11px] mt-1.5 line-clamp-2" style={{ color: "var(--ph-ink-default)" }}>{t.benefits}</div>
            </button>
          ))}
        </div>
      </Card>

      <Card testId="gr-l-filters">
        <TextInput value={q} onChange={setQ} placeholder="Search loyalty members…" testId="gr-l-search" />
        <div className="mt-4">
          <TabBar<string>
            testId="gr-l-tabs"
            active={tab}
            onChange={setTab}
            tabs={[{ key: "all", label: "All", count: LOYALTY_MEMBERS.length }].concat(
              tiers.map((t) => ({ key: t.key, label: t.name, count: t.members })),
            )}
          />
        </div>
      </Card>

      <Card padded={false} testId="gr-l-members">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Members · click for member card" title={`${filtered.length} member${filtered.length === 1 ? "" : "s"}`} />
        </div>
        <div className="p-2">
          {filtered.map((m) => {
            const t = tierByLtv(m.ltvEGP);
            return (
              <ListRow key={m.id} testId={`gr-lm-${m.id}`}
                onClick={() => { setSelected(m); setWsTab("card"); }}
                label={<span className="inline-flex items-center gap-2">{m.customer}
                  <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: `${t.color}22`, color: t.color, borderRadius: "var(--ph-radius-sm)" }}>{t.name}</span>
                </span>}
                sublabel={<>joined {m.joinedRelative} · last order {m.lastOrderRelative}</>}
                trailing={<span className="text-sm tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>LTV EGP {(m.ltvEGP / 1000).toFixed(0)}K</span>}
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Loyalty Member 360" testId="gr-lm-drawer" width={720}>
        {selected && drill && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: drill.cur.color }}>{drill.cur.name} · Member</div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{selected.customer}</div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<LoyTab>
                testId="gr-lm-ws-tabs"
                value={wsTab}
                onChange={setWsTab}
                options={[
                  { value: "card",      label: "Member card" },
                  { value: "benefits",  label: "Benefits"    },
                  { value: "progress",  label: "Progress"    },
                  { value: "upgrade",   label: "Upgrade"     },
                  { value: "spend",     label: "Spend"       },
                  { value: "wallet",    label: "Wallet"      },
                  { value: "history",   label: "History"     },
                  { value: "audit",     label: "Audit"       },
                ]}
              />
            </div>

            {wsTab === "card" && (
              <div data-testid="gr-lm-ws-card">
                <div className="p-5 mb-3" style={{ background: `linear-gradient(140deg, ${drill.cur.color} 0%, var(--ph-ink-strong) 100%)`, borderRadius: "var(--ph-radius-md)", color: "#fff" }}>
                  <div className="text-[10px] uppercase tracking-[0.18em] opacity-80">Hassan OS · Loyalty</div>
                  <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.6rem" }} className="mt-1">{selected.customer}</div>
                  <div className="text-xs mt-3 opacity-90">Tier</div>
                  <div className="text-xl font-semibold" style={{ letterSpacing: "0.14em" }}>{drill.cur.name.toUpperCase()}</div>
                  <div className="flex justify-between mt-3 text-[11px]">
                    <div><div className="opacity-80">Member since</div><div>{selected.joinedRelative}</div></div>
                    <div className="text-right"><div className="opacity-80">Card #</div><div className="font-mono">{selected.id.toUpperCase()}</div></div>
                  </div>
                </div>
                <Field label="Email template" inline><span className="text-xs font-mono">welcome-{drill.cur.key}.eml</span></Field>
              </div>
            )}

            {wsTab === "benefits" && (
              <div data-testid="gr-lm-ws-benefits">
                <Field label="Current tier" inline><span className="text-sm font-semibold" style={{ color: drill.cur.color }}>{drill.cur.name}</span></Field>
                <Field label="Benefits"><span className="text-sm">{drill.cur.benefits}</span></Field>
                <div className="mt-2 text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                  {drill.next ? `Unlock ${drill.next.name}: ${drill.next.benefits}` : "Highest tier reached. Bespoke perks apply."}
                </div>
              </div>
            )}

            {wsTab === "progress" && (
              <div data-testid="gr-lm-ws-progress">
                <Field label="Current LTV" inline><span className="text-sm tabular-nums font-semibold">EGP {selected.ltvEGP.toLocaleString()}</span></Field>
                <Field label="Progress"    inline><span className="text-sm tabular-nums">{drill.progressPct.toFixed(0)}%</span></Field>
                <div className="mt-1 h-2 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                  <div style={{ width: `${drill.progressPct}%`, height: "100%", background: drill.cur.color }} />
                </div>
                <div className="mt-2 text-[10px]" style={{ color: "var(--ph-ink-muted)" }}>
                  {drill.next ? `EGP ${drill.remainingEGP.toLocaleString()} remaining to reach ${drill.next.name}` : "Top of ladder."}
                </div>
              </div>
            )}

            {wsTab === "upgrade" && (
              <div data-testid="gr-lm-ws-upgrade">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Upgrade rules</div>
                {CANONICAL_TIERS.map((t) => (
                  <div key={t.key} className="grid items-center py-1.5" style={{ gridTemplateColumns: "1.4fr 3fr 1fr" }}>
                    <div className="text-sm font-medium" style={{ color: t.color }}>{t.name}</div>
                    <div className="text-sm" style={{ color: "var(--ph-ink-default)" }}>{t.benefits}</div>
                    <div className="text-sm tabular-nums text-right">EGP {(t.minLtvEGP / 1000).toFixed(0)}K+</div>
                  </div>
                ))}
              </div>
            )}

            {wsTab === "spend" && (
              <div data-testid="gr-lm-ws-spend">
                <Field label="Lifetime spend"    inline><span className="text-sm tabular-nums font-semibold">EGP {selected.ltvEGP.toLocaleString()}</span></Field>
                <Field label="Remaining to next" inline><span className="text-sm tabular-nums" style={{ color: drill.remainingEGP > 0 ? "var(--ph-warning)" : "var(--ph-success)" }}>EGP {drill.remainingEGP.toLocaleString()}</span></Field>
                {drill.customer360 && <>
                  <Field label="Lifetime orders" inline><span className="text-sm tabular-nums">{drill.customer360.lifetimeOrders}</span></Field>
                  <Field label="NPS"             inline><span className="text-sm tabular-nums">{drill.customer360.nps}</span></Field>
                </>}
              </div>
            )}

            {wsTab === "wallet" && (
              <div data-testid="gr-lm-ws-wallet">
                <div className="text-sm p-3 mb-2" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                  A wallet-ready pass (Apple Wallet / Google Pay) has been prepared for this member. The pass carries the member&rsquo;s tier, card #, and QR for in-store check-in.
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" testId={`gr-lm-wallet-apple-${selected.id}`} onClick={() => window.print()}>Apple Wallet .pkpass</Button>
                  <Button variant="secondary" testId={`gr-lm-wallet-google-${selected.id}`} onClick={() => window.print()}>Google Pay pass</Button>
                </div>
              </div>
            )}

            {wsTab === "history" && (
              <div data-testid="gr-lm-ws-history">
                <Field label="Joined"     inline><span className="text-sm">{selected.joinedRelative}</span></Field>
                <Field label="Last order" inline><span className="text-sm">{selected.lastOrderRelative}</span></Field>
                <AuditTrail collection="loyalty_members" recordId={selected.id} testId="gr-lm-history-audit" />
              </div>
            )}

            {wsTab === "audit" && (
              <div data-testid="gr-lm-ws-audit">
                <AuditTrail collection="loyalty_members" recordId={selected.id} testId="gr-lm-audit" />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
